import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { subDays } from 'date-fns';
import { Flex, Box } from '@qga/roo-ui/components';
import OccupantPicker from 'components/OccupantPicker';
import AvailabilityDatePicker from 'components/AvailabilityDatePicker';
import { hashObject } from 'lib/hash';
import { updateQuery as updateQueryAction } from 'store/propertyAvailability/propertyAvailabilityActions';
import { getFullKnownQuery, getQueryOccupants } from 'store/router/routerSelectors';
import { getIsExclusive } from 'store/property/propertySelectors';
import { getExclusiveOffer } from 'store/exclusiveOffer/exclusiveOfferSelectors';
import PageBlock from 'components/PageBlock';
import PayWith from '../PayWith';
import PayWithToggleMessage from 'components/PayWithToggleMessage';
import { PAYWITH_TOGGLE_ENABLED } from 'config';
import { useBreakpoints } from 'hooks/useBreakpoints';
import some from 'lodash/some';
import pick from 'lodash/pick';
import { LabelText } from 'components/Label';
import styled from '@emotion/styled';
import { themeGet } from 'styled-system';
import { FieldError } from '@qga/components';
import { useEndDate } from './../../../hooks/useEndDate';
import useRemovePointsPlusPay from 'hooks/optimizely/useRemovePointsPlusPay';

const labelOptions = { color: 'greys.alto' };

const InputErrorField = styled(FieldError)`
  margin-top: ${themeGet('space.2')};
`;

const withKey = (props) => ({ key: hashObject(props), ...props });

const SearchControls = ({ smallTablet = false }) => {
  const query = useSelector(getFullKnownQuery);
  const occupants = useSelector(getQueryOccupants);
  const isExclusive = useSelector(getIsExclusive);
  const offer = useSelector(getExclusiveOffer);
  const dispatch = useDispatch();
  const { isLessThanBreakpoint } = useBreakpoints();
  const { isReady, removePointsPlusPay } = useRemovePointsPlusPay();
  const pointsPlusPayEnabled = !(isReady && removePointsPlusPay);

  const isMobile = isLessThanBreakpoint(0);
  const { start: startDate, end: endDate } = offer?.travelDates || {};

  const [selectedDates, setSelectedDates] = useState({ startDate: query.checkIn, endDate: query.checkOut });
  const { checkoutDate, setCheckoutDate } = useEndDate();
  const occupantQueryProps = withKey({ occupants });
  const dateQueryProps = withKey({ selectedDates: selectedDates });
  const today = new Date();

  if (isExclusive && startDate && endDate) {
    //TODO: Update logic to open availability calendar on the offer.travelDates.start
    //Currently, because of a future start date, the redux logic will skip a month, not allowing the availability calendar to function properly.
    // by opening the calendar on today's date instead of a future date, we allow the fetchCalendar function to load every months from today.
    dateQueryProps.initialDisplayDate = today; //selectedStartDate < today ? today : selectedStartDate;
    dateQueryProps.boundaries = { start: subDays(new Date(startDate), 1), end: new Date(endDate) };
  }

  const hasEndDate = (endDate) => {
    setCheckoutDate(endDate);
  };

  const updateQuery = (query) => {
    const queryOccupants = pick(query, ['adults', 'children', 'infants']);
    const hasOccupants = some(occupants) || some(queryOccupants);
    const defaultOccupants = hasOccupants ? {} : { adults: 2, children: 0, infants: 0 };
    dispatch(updateQueryAction({ ...defaultOccupants, ...query }));
  };

  const onClickOutside = useCallback(() => {
    setSelectedDates({ startDate: undefined, endDate: undefined });
  }, [setSelectedDates]);

  useEffect(() => {
    setSelectedDates({ startDate: query.checkIn, endDate: query.checkOut });
  }, [query]);

  return (
    <PageBlock data-testid="search-controls-container" paddingLeft={[4, 6, 0]} paddingRight={[3, 6, 3]}>
      {smallTablet && (
        <Flex width="100%" justifyContent="flex-start">
          <LabelText fontSize="18px" ml={1}>
            When?
          </LabelText>
          <LabelText fontSize="18px" ml="555px">
            Guests
          </LabelText>
        </Flex>
      )}
      <Flex
        alignItems={['start', 'center', 'flex-end']}
        flexDirection={['column', 'column', 'row']}
        justifyContent={['start', 'center', 'space-between']}
        width="100%"
        data-testid="search-controls"
      >
        {isMobile && !smallTablet && (
          <Flex justifyContent="space-between" width="88%" pt="16px" pb="4px">
            <LabelText fontSize="18px">When?</LabelText>
            <LabelText fontSize="18px">Guests</LabelText>
          </Flex>
        )}
        <Flex justifyContent={['start', 'start', 'flex-start']} width={[1, 1, 'inherit']} pb={[6, 6, 0]} mb={[0, 0, 8]}>
          <Flex flexWrap="wrap" width={smallTablet ? '100%' : ['77%', '100%', '420px']}>
            <Box mr={4} data-testid="stay-date-picker" flexBasis={['95%', '100%', '400px']}>
              {!isExclusive && (
                <AvailabilityDatePicker
                  {...dateQueryProps}
                  labelOptions={labelOptions}
                  updateQuery={updateQuery}
                  hasEndDate={hasEndDate}
                  clearSelectedDates={onClickOutside}
                  anchorX="left"
                  isLimited={true}
                  backgroundColor={'transparent'}
                  zIndex={502}
                />
              )}
              {isExclusive && (
                <>
                  <AvailabilityDatePicker
                    {...dateQueryProps}
                    labelOptions={labelOptions}
                    updateQuery={updateQuery}
                    hasEndDate={hasEndDate}
                    clearSelectedDates={onClickOutside}
                    anchorX="left"
                    isLimited={false}
                    backgroundColor={'transparent'}
                    zIndex={502}
                  />
                </>
              )}
            </Box>
            {checkoutDate === null && (
              <Box flexBasis={['93%', '97%', '400px']}>
                <InputErrorField error={{ message: 'Select check-out date' }} data-testid="missing-checkOut-error" />
              </Box>
            )}
          </Flex>
          <Box flexBasis={['29%', '150px']}>
            <OccupantPicker {...occupantQueryProps} labelOptions={labelOptions} updateQuery={updateQuery} verboseInMobile={false} />
          </Box>
        </Flex>

        {PAYWITH_TOGGLE_ENABLED && isReady && (
          <Flex
            flexDirection="column"
            alignItems={pointsPlusPayEnabled ? undefined : 'flex-end'}
            width={['100%', 'auto', 'auto']}
            pb={checkoutDate === null ? '50px' : ''}
            data-testid="payWith-block"
          >
            <PayWithToggleMessage
              pointerDirection="bottom"
              pointerPosition={pointsPlusPayEnabled ? '50%' : isMobile ? '80%' : '90%'}
              mb={2}
            />
            <PayWith size="sm" />
          </Flex>
        )}
      </Flex>
    </PageBlock>
  );
};

SearchControls.propTypes = {
  smallTablet: PropTypes.bool,
};

export default SearchControls;
